import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";
import moment from "moment";
import { Spin } from "antd";
import axiosClient from "../../api/axios";
import LinkUpload from "../../api/LinkUpload";

// Import CSS mới
import './SinglePostView.css';

// =================== CÁC COMPONENT CON ===================

const Sidebar = ({ recentPosts }) => {
    const history = useHistory();
    const handleLinkClick = (e, slug) => {
        e.preventDefault();
        history.push(`/view/${slug}`);
    };

    return (
        <aside className="sidebar-col">
            <div className="widget">
                <h3 className="widget-title">Bài viết gần đây</h3>
                <ul className="widget-post-list">
                    {recentPosts.map(post => (
                        <li key={post.ID}>
                            <a href={`/view/${post.Slug}`} onClick={(e) => handleLinkClick(e, post.Slug)} title={post.Title}>
                                {post.ImageUrl ? (
                                    <img src={LinkUpload(post.ImageUrl)} alt={post.Title} className="thumb" />
                                ) : (
                                    <span className="placeholder-thumbnail thumb">
                                        <i className="fa-solid fa-image"></i>
                                    </span>
                                )}
                                <span className="title">{post.Title}</span>
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </aside>
    );
};

const RelatedPosts = ({ relatedPosts }) => {
    const history = useHistory();
    if (!relatedPosts || relatedPosts.length === 0) return null;

    const handleLinkClick = (e, slug) => {
        e.preventDefault();
        history.push(`/view/${slug}`);
    };

    return (
        <section className="related-posts-wrapper">
            <h2 className="related-posts-title">Bài viết liên quan</h2>
            <div className="related-posts-list">
                {relatedPosts.map(post => (
                    <a key={post.ID} href={`/view/${post.Slug}`} onClick={(e) => handleLinkClick(e, post.Slug)} className="post-card--list">
                        <div className="post-card__thumb">
                            {post.ImageUrl ? (
                                <img src={LinkUpload(post.ImageUrl)} alt={post.Title} />
                            ) : (
                                <span className="placeholder-thumbnail">
                                    <i className="fa-solid fa-image"></i>
                                </span>
                            )}
                        </div>
                        <div className="post-card__content">
                            <h4 className="post-card__title">{post.Title}</h4>
                            <div className="post-card__meta">
                                <span>{moment(post.PublishDate).format('DD/MM/YYYY')}</span>
                            </div>
                        </div>
                    </a>
                ))}
            </div>
        </section>
    );
};

const ShareLinks = ({ url, title }) => {
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    return (
        <div className="share-links">
            <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`} target="_blank" rel="noopener noreferrer" title="Chia sẻ lên Facebook"><i className="fab fa-facebook-f"></i></a>
            {/* Zalo share không có URL API công khai, có thể dùng 1 icon comment hoặc mail */}
            <a href={`mailto:?subject=${encodedTitle}&body=Xem bài viết này: ${encodedUrl}`} target="_blank" rel="noopener noreferrer" title="Gửi qua Email"><i className="fa-solid fa-envelope"></i></a>
        </div>
    );
};


// =================== COMPONENT CHÍNH ===================

const SinglePostView = () => {
    const location = useLocation();
    const history = useHistory();
    const slug = location.pathname.split('/view/').pop();

    const [article, setArticle] = useState(null);
    const [allArticles, setAllArticles] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const token = localStorage.getItem('token');
    const header = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        window.scrollTo(0, 0);
        setLoading(true);

        const fetchData = async () => {
            try {
                const [articlesRes, categoriesRes] = await Promise.all([
                    axiosClient.post('/Articles/GetArticlesAll', '', { headers: header }),
                    axiosClient.post('/Categories/GetCategoriesAll', '', { headers: header })
                ]);

                if (articlesRes.status === 200) {
                    const currentArticle = articlesRes.data.find(a => a.Slug === slug);
                    setArticle(currentArticle || null); // Đặt là null nếu không tìm thấy
                    setAllArticles(articlesRes.data.filter(a => a.Status === 1));

                    if (currentArticle) {
                        axiosClient.post(`/Articles/IncreaseView?IDArticle=${currentArticle.ID}`, '', { headers: header });
                    }
                }
                if (categoriesRes.status === 200) {
                    setAllCategories(categoriesRes.data);
                }

            } catch (error) {
                console.error("Lỗi khi tải bài viết:", error);
                setArticle(null);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [slug]);
    const derivedData = useMemo(() => {
        if (!article || allArticles.length === 0 || allCategories.length === 0) {
            return { category: null, relatedPosts: [], recentPosts: [] };
        }
        const category = allCategories.find(c => c.ID === article.IDCategory);
        const relatedPosts = allArticles
            .filter(a => a.IDCategory === article.IDCategory && a.ID !== article.ID)
            .slice(0, 4); // Lấy 4 bài liên quan
        const recentPosts = [...allArticles]
            .sort((a, b) => new Date(b.PublishDate) - new Date(a.PublishDate))
            .slice(0, 4); // Lấy 4 bài gần đây nhất

        return { category, relatedPosts, recentPosts };
    }, [article, allArticles, allCategories]);


    if (loading) {
        return <div className="container main-body" style={{ textAlign: 'center', padding: '100px' }}><Spin size="large" /></div>;
    }
    if (!article) {
        return <div className="container main-body" style={{ textAlign: 'center', padding: '100px' }}><h2>Bài viết không tồn tại.</h2></div>;
    }

    const { category, relatedPosts, recentPosts } = derivedData;
    const currentUrl = window.location.href;

    return (
        <div className="container main-body">
            <main>
                <div className="single-layout">
                    <div className="content-col">
                        <article className="post-full">
                            <header className="post-full-header">
                                <div className="breadcrumbs">
                                    <a href="/" onClick={(e) => { e.preventDefault(); history.push('/'); }}>Trang chủ</a>
                                    {category && (
                                        <>
                                            »
                                            <a href={`/category/${category.Slug}`} onClick={(e) => { e.preventDefault(); history.push(`/category/${category.Slug}`); }}>
                                                {category.Name}
                                            </a>
                                        </>
                                    )}
                                    »
                                    <span>{article.Title}</span>
                                </div>
                                <h1 className="post-full-title">{article.Title}</h1>
                                <div className="post-full-meta">
                                    <span>Ngày đăng: {moment(article.PublishDate).format('DD/MM/YYYY')}</span>
                                    {category && <span>Chuyên mục: <a href={`/category/${category.Slug}`} onClick={(e) => { e.preventDefault(); history.push(`/category/${category.Slug}`); }}>{category.Name}</a></span>}
                                </div>
                            </header>

                            {article.ImageUrl && (
                                <figure className="post-full-image">
                                    <img src={LinkUpload(article.ImageUrl)} alt={article.Title} />
                                </figure>
                            )}

                            <section
                                className="post-full-content"

                            >
                                <div className="table-responsive" dangerouslySetInnerHTML={{ __html: article.ArticleContent }} />
                            </section>

                            <footer className="post-full-footer">
                                <span className="share-links-title">Chia sẻ:</span>
                                <ShareLinks url={currentUrl} title={article.Title} />
                            </footer>
                        </article>

                        <RelatedPosts relatedPosts={relatedPosts} />
                    </div>

                    <Sidebar recentPosts={recentPosts} />
                </div>
            </main>
        </div>
    );
};

export default SinglePostView; 